import { defaultLanguage, resolveLocale } from '@plato/app-meta/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Returns the current locale (language plus any relevant region) resolved by
 * our locale rules. For a selected translation language, we attempt first to
 * match a locale in the users browser settings, and fall back to the default
 * otherwise.
 */
export function useResolvedLocale(): string {
    const { i18n } = useTranslation();
    const resolvedLanguage = i18n.resolvedLanguage ?? defaultLanguage;
    return useMemo(() => resolveLocale(resolvedLanguage, getNavigatorLanguages()), [resolvedLanguage]);
}

/**
 * Returns the current language resolved by the i18next logic. This will always
 * match an available translation language, and may include a region in the case
 * that a regional translation is active.
 */
export function useResolvedLanguage(): string {
    const { i18n } = useTranslation();
    return i18n.resolvedLanguage ?? defaultLanguage;
}

/**
 * Returns a list of locales defined by the user's browser settings.
 */
export function getNavigatorLanguages(): string[] {
    if (
        typeof navigator !== 'undefined' &&
        Array.isArray(navigator.languages as unknown) &&
        navigator.languages.length > 0
    ) {
        return [...navigator.languages];
    }
    return [];
}
